import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.addVirtualFileSystem(pdfFonts);

/**
 * Konwertuje obraz do base64
 */
async function getBase64Image(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = function() {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL('image/png');
            resolve(dataURL);
        };
        img.onerror = reject;
        img.src = url;
    });
}

/**
 * Formatuje datę do formatu YYYY-MM-DD
 */
function formatDate(dateString) {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
}

/**
 * Grupuje oceny według kwartałów
 */
function groupEvaluationsByQuarter(evaluations) {
    const quarters = {};

    evaluations.forEach(evaluation => {
        const date = new Date(evaluation.deliveryDate);
        const year = date.getFullYear();
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        const key = `${year}-Q${quarter}`;

        if (!quarters[key]) {
            quarters[key] = {
                year,
                quarter,
                quality: [],
                delivery: [],
                price: [],
                total: []
            };
        }

        quarters[key].quality.push(evaluation.rating.quality);
        quarters[key].delivery.push(evaluation.rating.delivery);
        quarters[key].price.push(evaluation.rating.price);
        quarters[key].total.push(evaluation.totalScore);
    });

    // Oblicz średnie dla każdego kwartału
    Object.keys(quarters).forEach(key => {
        const q = quarters[key];
        q.avgQuality = Math.round((q.quality.reduce((a, b) => a + b, 0) / q.quality.length) * 10) / 10;
        q.avgDelivery = Math.round((q.delivery.reduce((a, b) => a + b, 0) / q.delivery.length) * 10) / 10;
        q.avgPrice = Math.round((q.price.reduce((a, b) => a + b, 0) / q.price.length) * 10) / 10;
        q.avgTotal = Math.round((q.total.reduce((a, b) => a + b, 0) / q.total.length) * 10) / 10;
    });

    return quarters;
}

/**
 * Generuje dokument PDF z kartą oceny dostawcy
 * @param {Object} customer - Dane dostawcy/klienta
 * @param {Array} evaluations - Lista ocen dostaw
 * @param {Array} qualifications - Lista kwalifikacji dostawcy
 * @param {Function} getUserName - Funkcja do pobrania nazwy użytkownika
 */
export async function generateSupplierEvaluationCardPDF(customer, evaluations, qualifications, getUserName) {
    // Załaduj logo jako base64
    let logoBase64 = null;
    try {
        logoBase64 = await getBase64Image('/marss-logo.png');
    } catch (error) {
        console.warn('Nie udało się załadować logo:', error);
    }

    // Pobierz unikalne kategorie dostaw
    const categories = [...new Set(evaluations.map(e => e.goodsCategory))].filter(Boolean);

    // Grupuj oceny według kwartałów
    const quarters = groupEvaluationsByQuarter(evaluations);
    const quarterKeys = Object.keys(quarters).sort();

    // Przygotuj nagłówki kolumn dla kwartałów (max 12 kwartałów - 3 lata)
    const quarterHeaders = [];
    quarterKeys.slice(0, 12).forEach(key => {
        const q = quarters[key];
        quarterHeaders.push({
            text: `${q.year}\nQ${q.quarter}`,
            style: 'tableHeader',
            alignment: 'center'
        });
    });

    // Uzupełnij puste kolumny jeśli jest mniej niż 12 kwartałów
    while (quarterHeaders.length < 12) {
        quarterHeaders.push({ text: '', style: 'tableHeader' });
    }

    // Przygotuj dane dla tabeli kwalifikacji
    const qualificationTableBody = [
        [
            { text: 'Lp', style: 'tableHeader', alignment: 'center', rowSpan: 2 },
            { text: 'Kryterium oceny\n(punktacja 1 - 5)', style: 'tableHeader', alignment: 'center', rowSpan: 2 },
            { text: 'Rok / Kwartał', style: 'tableHeader', alignment: 'center', colSpan: 12 },
            ...Array(11).fill({})
        ],
        [
            {},
            {},
            ...quarterHeaders
        ]
    ];

    // Dodaj wiersze z kryteriami
    const criteria = [
        { name: 'Jakość dostarczanych wyrobów/usług', key: 'avgQuality' },
        { name: 'Terminowość realizacji dostaw wyrobów/usług', key: 'avgDelivery' },
        { name: 'Proponowane warunki płatności', key: 'avgPrice' }
    ];

    criteria.forEach((criterion, idx) => {
        const row = [
            { text: (idx + 1).toString(), style: 'tableCell', alignment: 'center' },
            { text: criterion.name, style: 'tableCell' }
        ];

        quarterKeys.slice(0, 12).forEach(key => {
            const value = quarters[key][criterion.key];
            row.push({ text: value.toString(), style: 'tableCell', alignment: 'center' });
        });

        // Uzupełnij puste kolumny
        while (row.length < 14) {
            row.push({ text: '', style: 'tableCell' });
        }

        qualificationTableBody.push(row);
    });

    // Dodaj wiersz "Suma punktów"
    const sumRow = [
        { text: '', style: 'tableCell' },
        { text: 'Suma punktów:', style: 'tableCell', bold: true }
    ];
    quarterKeys.slice(0, 12).forEach(key => {
        sumRow.push({
            text: quarters[key].avgTotal.toString() + '/15',
            style: 'tableCell',
            alignment: 'center',
            bold: true
        });
    });
    while (sumRow.length < 14) {
        sumRow.push({ text: '', style: 'tableCell' });
    }
    qualificationTableBody.push(sumRow);

    // Dodaj wiersze dla decyzji i daty
    const decisionRow = [
        { text: '', style: 'tableCell' },
        { text: 'Decyzja dotycząca kwalifikacji (Z, N):', style: 'tableCell' }
    ];
    const dateSignRow = [
        { text: '', style: 'tableCell' },
        { text: 'Kto i kiedy zakwalifikował:', style: 'tableCell' }
    ];

    quarterKeys.slice(0, 12).forEach(key => {
        const q = quarters[key];
        const qual = qualifications.find(r => r.year === q.year && r.quarter === q.quarter);
        if (qual) {
            decisionRow.push({
                text: qual.qualified ? 'Z' : 'N',
                style: 'tableCell',
                alignment: 'center',
                bold: true,
                color: qual.qualified ? '#1a7c1a' : '#b00000'
            });
            const dateSignText = `${formatDate(qual.createdAt)}\n${getUserName(qual.createdBy)}`
                + (qual.comments ? `\n${qual.comments}` : '');
            dateSignRow.push({ text: dateSignText, style: 'tableCell', fontSize: 7 });
        } else {
            decisionRow.push({ text: '', style: 'tableCell' });
            dateSignRow.push({ text: '', style: 'tableCell' });
        }
    });

    // Uzupełnij puste kolumny jeśli jest mniej niż 12 kwartałów
    while (decisionRow.length < 14) {
        decisionRow.push({ text: '', style: 'tableCell' });
        dateSignRow.push({ text: '', style: 'tableCell' });
    }

    qualificationTableBody.push(decisionRow);
    qualificationTableBody.push(dateSignRow);

    // Przygotuj tabelę notatek
    const notesTableBody = [
        [
            { text: 'Lp', style: 'tableHeader', alignment: 'center' },
            { text: 'Data', style: 'tableHeader', alignment: 'center' },
            { text: 'Opis zdarzenia', style: 'tableHeader', alignment: 'center' },
            { text: 'Uwagi', style: 'tableHeader', alignment: 'center' }
        ]
    ];

    // Dodaj notatki z ocen (ostatnie 5)
    const evaluationsWithComments = evaluations
        .filter(e => e.comments)
        .slice(0, 5);

    evaluationsWithComments.forEach((evaluation, idx) => {
        notesTableBody.push([
            { text: (idx + 1).toString(), style: 'tableCell', alignment: 'center' },
            { text: formatDate(evaluation.deliveryDate), style: 'tableCell', alignment: 'center' },
            { text: `Dostawa ${evaluation.order?.documentNo || '—'}: ${evaluation.comments}`, style: 'tableCell' },
            { text: getUserName(evaluation.createdBy), style: 'tableCell' }
        ]);
    });

    // Uzupełnij do 5 wierszy
    while (notesTableBody.length < 6) {
        notesTableBody.push([
            { text: notesTableBody.length.toString(), style: 'tableCell', alignment: 'center' },
            { text: '', style: 'tableCell' },
            { text: '', style: 'tableCell' },
            { text: '', style: 'tableCell' }
        ]);
    }

    // Definicja dokumentu PDF
    const docDefinition = {
        pageSize: 'A4',
        pageOrientation: 'landscape',
        pageMargins: [30, 60, 30, 40],

        content: [
            // Logo (jeśli zostało załadowane)
            ...(logoBase64 ? [{
                image: logoBase64,
                width: 120,
                alignment: 'center',
                margin: [0, 0, 0, 10]
            }] : []),

            // Tytuł
            {
                text: 'KARTA OCENY DOSTAWCY',
                style: 'title',
                alignment: 'center',
                margin: [0, 0, 0, 15]
            },

            // Dane dostawcy
            {
                table: {
                    widths: ['50%', '50%'],
                    body: [
                        [
                            {
                                stack: [
                                    { text: 'Dane dostawcy:', style: 'sectionHeader', margin: [0, 0, 0, 5] },
                                    { text: `Nazwa firmy: ${customer.name || '—'}`, style: 'infoText' },
                                    { text: `Adres: ${customer.street || '—'}, ${customer.postCode || '—'} ${customer.city || '—'}`, style: 'infoText' }
                                ],
                                border: [true, true, true, true]
                            },
                            {
                                stack: [
                                    { text: 'Telefon:', style: 'sectionHeader', margin: [0, 0, 0, 5] },
                                    { text: customer.phone || '—', style: 'infoText' }
                                ],
                                border: [true, true, true, true]
                            }
                        ]
                    ]
                },
                margin: [0, 0, 0, 10]
            },

            // Kategorie dostaw
            {
                table: {
                    widths: ['100%'],
                    body: [
                        [{
                            text: `Kategorie dostaw: ${categories.join(', ') || '—'}`,
                            style: 'infoText'
                        }]
                    ]
                },
                margin: [0, 0, 0, 15]
            },

            // Tabela kwalifikacji
            {
                table: {
                    headerRows: 2,
                    widths: [25, 'auto', ...Array(12).fill('*')],
                    body: qualificationTableBody
                },
                layout: {
                    hLineWidth: function () { return 1; },
                    vLineWidth: function () { return 1; },
                    hLineColor: function () { return '#000000'; },
                    vLineColor: function () { return '#000000'; }
                },
                margin: [0, 0, 0, 15]
            },

            // Tabela notatek
            {
                text: 'NOTATKI ZE WSPÓŁPRACY *',
                style: 'sectionHeader',
                margin: [0, 0, 0, 5]
            },
            {
                table: {
                    headerRows: 1,
                    widths: [30, 80, '*', 100],
                    body: notesTableBody
                },
                layout: {
                    hLineWidth: function () { return 1; },
                    vLineWidth: function () { return 1; },
                    hLineColor: function () { return '#000000'; },
                    vLineColor: function () { return '#000000'; }
                }
            }
        ],

        styles: {
            title: {
                fontSize: 14,
                bold: true,
                margin: [0, 0, 0, 10]
            },
            sectionHeader: {
                fontSize: 11,
                bold: true
            },
            infoText: {
                fontSize: 10,
                margin: [0, 2, 0, 2]
            },
            tableHeader: {
                bold: true,
                fontSize: 9,
                margin: [3, 3, 3, 3]
            },
            tableCell: {
                fontSize: 8,
                margin: [3, 3, 3, 3]
            }
        }
    };

    // Generuj i pobierz PDF
    const fileName = `karta_oceny_dostawcy_${customer.name?.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdfMake.createPdf(docDefinition).download(fileName);
}
